import React from 'react';
import { Link } from 'react-router-dom';
import { Trophy, Users, Calendar, Shirt, Flag, Target, ArrowRight } from 'lucide-react';
import { SectionHeading, FeatureCard, ImageBlock, KpiTile } from '@/components/Pieces';
import ContactForm from '@/components/ContactForm';
import FAQ from '@/components/FAQ';
import usePageMeta from '@/hooks/usePageMeta';
import { img } from '@/lib/site';

const SPORTS = [
  { name: 'Basketball', desc: 'Indoor 5v5 and 3v3 corporate leagues.' },
  { name: 'Flag Football', desc: '7v7 leagues, fall and spring seasons.' },
  { name: 'Soccer', desc: 'Outdoor and indoor coed leagues.' },
  { name: 'Volleyball', desc: 'Coed indoor and grass tournaments.' },
  { name: 'Softball', desc: 'Coed slow-pitch corporate leagues.' },
  { name: 'Team Challenges', desc: 'Field day, obstacle, and culture events.' },
  { name: 'And More', desc: 'Custom sports built to your workforce.' },
];

const FOCUS = [
  { icon: Users, title: 'Interdepartment Competition', desc: 'Engineered to break silos and surface emerging leaders.' },
  { icon: Calendar, title: 'Seasonal Championships', desc: 'Structured seasons, playoffs, and championship nights.' },
  { icon: Shirt, title: 'Team Identity & Branding', desc: 'Jerseys, branded schedules, team identity systems.' },
  { icon: Flag, title: 'Family Participation', desc: 'Family-friendly events that build community and retention.' },
  { icon: Target, title: 'Leadership Through Sport', desc: 'Captains, accountability, communication, decision-making.' },
  { icon: Trophy, title: 'Tournaments & Corporate Cups', desc: 'Cross-company invitationals and corporate cup events.' },
];

const HR_OUTCOMES = [
  'Employee engagement',
  'Participation rates',
  'Retention improvement',
  'Morale & culture lift',
  'Cross department collaboration',
  'Team communication',
  'Workforce culture index',
  'Manager-level visibility',
];

export default function CorporateLeaguesPage() {
  usePageMeta({
    title: 'Corporate Sports Leagues & Team Building | Ball Raw Performance',
    description:
      'Corporate sports leagues, team competitions, and culture experiences designed to improve employee engagement, morale, retention, and cross-department collaboration. Built for HR, People, and Operations leaders across Southern California.',
    path: '/corporate-leagues',
  });
  return (
    <div data-testid="page-corporate-leagues">
      <section className="relative bg-[#0A0A0A] text-white overflow-hidden">
        <div className="absolute inset-0">
          <img src={img('leagues_basketball')} alt="Corporate basketball" className="w-full h-full object-cover opacity-45" />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/85 to-black/30" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-36">
          <p className="brp-overline brp-reveal">Corporate Games & Competitive Leagues</p>
          <h1 className="brp-reveal brp-delay-1 font-display uppercase font-bold text-5xl md:text-7xl lg:text-8xl leading-[0.92] mt-6 max-w-4xl">
            Engagement, <span className="text-[#D4AF37]">Played</span> Like a Season.
          </h1>
          <p className="brp-reveal brp-delay-2 mt-8 text-lg md:text-xl text-gray-300 max-w-2xl">
            Corporate sports leagues and team competitions designed to improve employee engagement, morale, retention, workplace culture, and cross-department collaboration — investments in culture and connection, not just sports.
          </p>
          <div className="brp-reveal brp-delay-3 mt-10 flex flex-wrap gap-4">
            <Link to="/partnerships#contact" className="btn-brp-primary" data-testid="leagues-cta-discovery">
              Launch a League <ArrowRight size={16} />
            </Link>
            <Link to="/partnerships" className="btn-brp-outline-light" data-testid="leagues-cta-partner">
              Request Partnership Information
            </Link>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 px-6 md:px-10 bg-white">
        <div className="max-w-[1400px] mx-auto">
          <SectionHeading
            overline="Sports Included"
            title="A League For Every Workforce."
            subtitle="Choose what fits your culture. Mix and match across quarters or run a full year of programming. Every season is engineered to lift engagement, morale, and team connection."
          />
          <div className="mt-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-px bg-[#E5E5E5]">
            {SPORTS.map((s, i) => (
              <div key={s.name} className="bg-white p-8 hover:bg-[#0A0A0A] hover:text-white transition-colors duration-300 group cursor-default" data-testid={`sport-${i}`}>
                <div className="font-display text-3xl md:text-4xl font-bold uppercase tracking-tight group-hover:text-[#D4AF37] transition-colors">
                  {s.name}
                </div>
                <p className="mt-3 text-sm text-[#555] group-hover:text-gray-300 transition-colors">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 px-6 md:px-10 bg-[#F9F9F9]">
        <div className="max-w-[1400px] mx-auto">
          <SectionHeading
            overline="Focus Areas"
            title="More Than Games. A Culture Operating System."
            subtitle="Organizations are investing in employee experience, workplace connection, and culture — not just sports. Every league is engineered around the outcomes HR and People leaders need."
          />
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {FOCUS.map((f, i) => (
              <FeatureCard key={f.title} icon={f.icon} title={f.title} desc={f.desc} accent testId={`focus-${i}`} />
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 px-6 md:px-10 bg-[#0A0A0A] text-white">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <SectionHeading
              light
              overline="HR & PEO Outcomes"
              title="The Metrics Your Leaders Already Track."
              subtitle="Corporate leagues are not a perk — they are infrastructure for engagement, retention, and culture. We deliver them with KPIs."
            />
          </div>
          <div className="lg:col-span-7 grid grid-cols-2 gap-px bg-[#D4AF37]/30">
            {HR_OUTCOMES.map((o, i) => (
              <div key={o} className="bg-[#0A0A0A] p-6 border border-[#1f1f1f]" data-testid={`outcome-${i}`}>
                <div className="text-[#D4AF37] font-display text-3xl font-bold">0{i + 1}</div>
                <div className="mt-2 font-display uppercase tracking-wider text-sm">{o}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 px-6 md:px-10 bg-white">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <ImageBlock src={img('divisions_corporate')} alt="Corporate league" ratio="aspect-[4/3]" caption="Team Identity Systems" />
          </div>
          <div className="lg:col-span-6">
            <SectionHeading
              overline="What's Included"
              title="Turn-Key League Operations."
            />
            <ul className="mt-8 space-y-3">
              {[
                'Season scheduling, refs, venues, and logistics',
                'Custom jerseys and team identity systems',
                'Standings, stats, and championship events',
                'Family days and community participation tracks',
                'HR-ready engagement dashboards',
              ].map((t) => (
                <li key={t} className="flex items-start gap-3">
                  <span className="mt-1 w-2 h-2 bg-[#D4AF37] inline-block flex-shrink-0" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
            <Link to="/partnerships#contact" className="btn-brp-outline-dark mt-10" data-testid="leagues-cta-info">
              Request Partnership Information
            </Link>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 px-6 md:px-10 bg-white">
        <div className="max-w-[1400px] mx-auto">
          <FAQ
            testIdPrefix="leagues-faq"
            overline="Frequently Asked"
            title="Frequently Asked Questions"
            items={[
              {
                q: 'How do Corporate Leagues improve employee engagement?',
                a: 'Corporate Leagues give employees opportunities to build relationships outside of their daily responsibilities. Stronger workplace connections help support teamwork, communication, and organizational culture.',
              },
              {
                q: 'Why do organizations invest in Corporate Leagues?',
                a: 'Organizations use Corporate Leagues to strengthen employee engagement, encourage collaboration, and create memorable team experiences. These programs also support recruitment, retention, and employee recognition initiatives.',
              },
              {
                q: 'What sports and activities do you organize?',
                a: 'We organize basketball, volleyball, soccer, kickball, dodgeball, pickleball, flag football, and customized team competitions. Every league is designed around your workforce and organizational goals.',
              },
              {
                q: 'How much work is required from HR or Operations?',
                a: 'Our team manages planning, scheduling, coordination, and league operations. Your HR and Operations teams remain informed while staying focused on their day to day responsibilities.',
              },
              {
                q: 'Can families participate in Corporate League events?',
                a: 'Yes. Organizations can include family friendly events that encourage employees and their loved ones to participate together and strengthen workplace community.',
              },
              {
                q: 'How quickly can we launch a Corporate League?',
                a: 'Launch timelines depend on workforce size, scheduling, and venue availability. We work with your organization to create a smooth and successful rollout.',
              },
            ]}
          />
        </div>
      </section>

      <section className="py-20 md:py-28 px-6 md:px-10 bg-[#F9F9F9]" id="contact">
        <div className="max-w-[900px] mx-auto">
          <ContactForm
            formType="programs"
            variant="dark"
            title="Build Your Corporate League"
            subtitle="Tell us your workforce size and sports interest. We'll come back with a tailored proposal."
            showRole
            showEmployees
            showIndustry
            showInterest
            ctaLabel="Request Proposal"
            testIdPrefix="leagues-contact"
          />
        </div>
      </section>
    </div>
  );
}
